import { useDisplayMessage } from '../hooks/useDisplayMessage'
import { useSavedMoment } from '../hooks/useSavedMoment'
import { useSpotlight } from '../hooks/useSpotlight'
import type { MessageSource } from '../lib/messageSource'
import { assertNever, type DisplayMessage } from '../types/display'
import Stage, { type StageMoment } from './stage/Stage'
import OrderPanel from './order/OrderPanel'
import ApprovedMoment from './moments/ApprovedMoment'
import ChangeMoment from './moments/ChangeMoment'
import ErrorMoment from './moments/ErrorMoment'
import PaymentMoment from './moments/PaymentMoment'

/** The stage follows the order side: the amount while paying, thanks once
 *  it is paid. A failed payment leaves the stage as it was. */
function stageMomentFor(message: DisplayMessage | null): StageMoment {
  switch (message?.type) {
    case 'scan_mobilepay_qr_code':
      return { kind: 'pay', amount: message.amount }
    case 'scan_receipt_qr_code':
      return { kind: 'thanks', scan: true }
    case 'change_given':
    case 'cashback_given':
    case 'payment_approved':
      return { kind: 'thanks' }
    default:
      return null
  }
}

function momentFor(message: DisplayMessage | null, currency: string) {
  if (!message) return null

  switch (message.type) {
    case 'cart_update':
      return null
    case 'scan_mobilepay_qr_code':
      return <PaymentMoment kind="mobilepay" url={message.url} amount={message.amount} currency={currency} />
    case 'scan_receipt_qr_code':
      return <PaymentMoment kind="receipt" url={message.url} amount={message.amount} currency={currency} />
    case 'change_given':
      return <ChangeMoment kind="change" amount={message.change} currency={message.currency ?? currency} />
    case 'cashback_given':
      return <ChangeMoment kind="cashback" amount={message.cashback} currency={currency} />
    case 'payment_approved':
      return <ApprovedMoment amount={message.amount} currency={message.currency ?? currency} />
    case 'payment_error':
      return <ErrorMoment message={message.message} />
    default:
      return assertNever(message)
  }
}

export default function Display({ placeId, source }: { placeId: string; source: MessageSource }) {
  const { message, cart, status, place } = useDisplayMessage(source)
  const currency = cart?.currency ?? place?.currency ?? 'DKK'
  // The line the cashier just rang up, lit in the list.
  const spotlight = useSpotlight(cart?.items)
  const saved = useSavedMoment(cart)

  return (
    // h-viewport (index.css): the height a tablet browser actually shows, so
    // the amount to pay is never under its address bar. Side by side the
    // shop's canvas takes the right, the larger part; a tall screen stacks
    // the canvas over the order.
    <div className="flex h-viewport w-screen flex-row-reverse bg-white portrait:flex-col">
      <Stage placeId={placeId} place={place} cachedLogo={!source.brandingNeedsGrant} ordering={!!cart?.items?.length} currency={currency} moment={stageMomentFor(message)} saved={saved} />
      <OrderPanel cart={cart} currency={currency} spotlight={spotlight} status={status}>
        {momentFor(message, currency)}
      </OrderPanel>
    </div>
  )
}
